import { AfterViewInit, ChangeDetectionStrategy, Component, inject, NgZone, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { NgxTurnstileModule } from "ngx-turnstile";
import { AuthService } from '../auth.service';
import { FormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthContext } from '../services/auth.context';
import * as CryptoJS from 'crypto-js';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    NgxTurnstileModule,
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements OnInit, AfterViewInit {

  private snackBar = inject(MatSnackBar);

  userName: string = '';
  password: string = '';
  disableSignInButton: boolean = true;
  captchaResponse: string = '';

  constructor(private router: Router,
    private authService: AuthService,
    private authContext: AuthContext,
    private ngZone: NgZone) { }

  ngOnInit(): void {
    this.verifyLogin();
  }

  verifyLogin() {
    const userLoggedIn: Boolean = Boolean(localStorage.getItem('userLoggedIn'));

    if (userLoggedIn) {
      this.router.navigate(['/app/home'], { replaceUrl: true });
    } else {
      this.router.navigate(['/app/login']);
    }
  }

  ngAfterViewInit() {
    const input = document.querySelector('input[matInput]');
    if (input) {
      input.setAttribute('autocomplete', 'off');
    }
  }

  hide = signal(true);
  clickEvent(event: MouseEvent) {
    this.hide.set(!this.hide());
    event.stopPropagation();
  }

  async saveLogin() {
    if (this.userName.trim().length < 1 || this.password.trim().length < 1) {
      this.openSnackBar("Please enter valid username & password", "Okay");
    } else {
      const secret = environment.ivBase64Secret;
      const sha256Key = CryptoJS.SHA256(secret);
      const iv = CryptoJS.lib.WordArray.random(16);
      const ivBase64 = CryptoJS.enc.Base64.stringify(iv);
      const encryptedUsername = CryptoJS.AES.encrypt(this.userName, sha256Key, { iv: iv }).toString();
      const encryptedPassword = CryptoJS.AES.encrypt(this.password, sha256Key, { iv: iv }).toString();

      this.authService.loginUser({
        userName: encryptedUsername,
        password: encryptedPassword,
        instance: ivBase64,
        captchaResponse: this.captchaResponse,
        source: 'WEB'
      }).subscribe({
        next: async (response) => {
          // console.log('POST response:', response);
          if (response.loginSuccess) {
            this.authContext.setToken(response.token);
            localStorage.setItem('userLoggedIn', "true");
            try {
              const userInfo = await firstValueFrom(this.authService.getUserInfo());
              await this.authContext.setUserInfo(userInfo);  // Wait for setUserInfo to finish

              // Run navigation within NgZone
              this.ngZone.run(() => {
                this.router.navigate(['/app/home'], { replaceUrl: true });
              });
            } catch (error) {
              console.error('Error fetching user info:', error);
              this.openSnackBar("Failed to fetch user info.", "Okay");
              this.reloadCurrentRoute();
            }
          }
        },
        error: (error) => {
          console.error('POST error:', error);
          this.openSnackBar(error.error.message, "Okay");
          this.reloadCurrentRoute();
        },
      });
    }
  }

  reloadCurrentRoute() {
    let currentUrl = this.router.url;
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([currentUrl]);
    });
  }

  openSnackBar(message: string, action?: string) {
    this.snackBar.open(message, action, { duration: 5 * 1000 }); // d5 seconds
  }

  sendCaptchaResponse(captchaRes: any) {
    console.log(`Resolved captcha with response: ${captchaRes}`);
    if (captchaRes) {
      this.captchaResponse = captchaRes;
      this.disableSignInButton = false;
    }
  }

  goToForgotPassword() {
    this.ngZone.run(() => {
      this.router.navigate(['/app/forgot-password']);
    });
  }

  goToSignUp() {
    this.ngZone.run(() => {
      this.router.navigate(['/app/sign-up']);
    });
  }
}
